import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Chip, TextField, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PeopleIcon from '@mui/icons-material/People';
import api from '../services/api';
import { User } from '../types/auth';
import UserModal from '../components/users/UserModal';
import { designTokens } from '../theme/designTokens';
import { useAuth } from '../context/AuthContext';

const UsersPage = () => {
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const { user: currentUser } = useAuth();
    const [tabValue, setTabValue] = useState(0);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [successDialogOpen, setSuccessDialogOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState<string | null>(null);

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const res = await api.get('/api/users');
            setUsers(res.data);
            setLoading(false);
        } catch (err) {
            console.error("Error fetching users", err);
            setLoading(false);
        }
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const handleAddUser = () => {
        setEditingUser(null);
        setModalOpen(true);
    };

    const handleEditUser = (user: User) => {
        setEditingUser(user);
        setModalOpen(true);
    };

    const handleDeleteUser = (userId: string) => {
        setUserToDelete(userId);
        setDeleteDialogOpen(true);
    };

    const confirmDeleteUser = async () => {
        if (!userToDelete) return;

        try {
            await api.delete(`/api/users/${userToDelete}`);
            setDeleteDialogOpen(false);
            setSuccessDialogOpen(true);
            fetchUsers();
            setUserToDelete(null);
        } catch (err) {
            console.error("Error deleting user", err);
            // Optionally handle error state here
            setDeleteDialogOpen(false);
        }
    };

    const handleSaveUser = async (data: any) => {
        try {
            if (editingUser) {
                await api.put(`/api/users/${editingUser._id}`, data);
            } else {
                await api.post('/api/users', data);
            }
            fetchUsers();
        } catch (err) {
            console.error("Error saving user", err);
        }
    };

    const getFilteredUsers = () => {
        // Dynamic tab mapping based on visible tabs
        const tabs = ['all'];
        if (currentUser?.role === 'super-admin') {
            tabs.push('super-admin', 'of-admin');
        }
        tabs.push('trainer', 'learner');

        const selectedTab = tabs[tabValue];
        let filtered = users;

        if (selectedTab !== 'all') {
            filtered = filtered.filter(u => u.role === selectedTab);
        }

        if (searchTerm) {
            const lowerSearch = searchTerm.toLowerCase();
            filtered = filtered.filter(u =>
                u.firstName.toLowerCase().includes(lowerSearch) ||
                u.lastName.toLowerCase().includes(lowerSearch) ||
                u.email.toLowerCase().includes(lowerSearch)
            );
        }

        return filtered;
    };

    const getRoleColor = (role: string) => {
        switch (role) {
            case 'super-admin': return 'error';
            case 'of-admin': return 'warning';
            case 'trainer': return 'info';
            case 'learner': return 'success';
            default: return 'default';
        }
    };

    const getRoleLabel = (role: string) => {
        switch (role) {
            case 'super-admin': return 'Administrateur';
            case 'of-admin': return 'Responsable OF';
            case 'trainer': return 'Formateur';
            case 'learner': return 'Apprenant';
            default: return role;
        }
    };

    if (loading) return <div>Loading...</div>;

    return (
        <Box sx={{ p: 4, maxWidth: 1400, mx: 'auto' }}>
            <Box sx={{ ...designTokens.pageHeader, mb: 4 }}>
                <Box>
                    <Typography variant="h4" sx={{ ...designTokens.pageTitle, mb: 1 }}>
                        <PeopleIcon fontSize="large" sx={{ mr: 2, verticalAlign: 'middle' }} />
                        Gestion des Utilisateurs
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Gérez les comptes, les rôles et les permissions de votre organisation.
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddUser}
                    sx={{
                        ...designTokens.actionButton,
                        px: 4,
                        py: 1.5,
                        borderRadius: 2,
                        boxShadow: '0 4px 14px 0 rgba(0,118,255,0.39)'
                    }}
                >
                    Ajouter un Utilisateur
                </Button>
            </Box>


            <Paper sx={{
                mb: 3,
                p: 2,
                bgcolor: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(20px)',
                boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                border: '1px solid rgba(255, 255, 255, 0.5)',
                borderRadius: 3,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 2
            }}>
                <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    indicatorColor="primary"
                    textColor="primary"
                    sx={{
                        '& .MuiTab-root': {
                            fontWeight: 'bold',
                            textTransform: 'none',
                            fontSize: '0.95rem',
                            minHeight: 48,
                            px: 3
                        },
                        '& .MuiTabs-indicator': {
                            height: 3,
                            borderRadius: '3px 3px 0 0'
                        }
                    }}
                >
                    <Tab label="Tous" />
                    {currentUser?.role === 'super-admin' && <Tab label="Administrateurs" />}
                    {currentUser?.role === 'super-admin' && <Tab label="OF Admins" />}
                    <Tab label="Formateurs" />
                    <Tab label="Apprenants" />
                </Tabs>

                <TextField
                    placeholder="Rechercher un utilisateur..."
                    variant="outlined"
                    size="small"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{
                        width: 300,
                        '& .MuiOutlinedInput-root': {
                            bgcolor: 'white',
                            borderRadius: 2
                        }
                    }}
                />
            </Paper>


            <TableContainer component={Paper} sx={{
                ...designTokens.glassCard,
                boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.07)',
                borderRadius: 3,
                overflow: 'hidden'
            }}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                            <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary', py: 2 }}>UTILISATEUR</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary', py: 2 }}>EMAIL</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary', py: 2 }}>RÔLE</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary', py: 2 }}>DATE D'INSCRIPTION</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold', color: 'text.secondary', py: 2 }}>ACTIONS</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {getFilteredUsers().map((user) => (
                            <TableRow key={user._id} sx={{
                                '&:last-child td, &:last-child th': { border: 0 },
                                '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' },
                                transition: 'background-color 0.2s'
                            }}>
                                <TableCell sx={{ fontWeight: 600, py: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Box sx={{
                                            width: 40,
                                            height: 40,
                                            borderRadius: '50%',
                                            bgcolor: 'primary.light',
                                            color: 'primary.main',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontWeight: 'bold',
                                            fontSize: '1rem'
                                        }}>
                                            {user.firstName[0]}{user.lastName[0]}
                                        </Box>
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                                {user.firstName} {user.lastName}
                                            </Typography>
                                            {user.organizationId && typeof user.organizationId === 'object' && (
                                                <Typography variant="caption" color="text.secondary">
                                                    {user.organizationId.name}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ color: 'text.secondary' }}>{user.email}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={getRoleLabel(user.role)}
                                        color={getRoleColor(user.role) as any}
                                        size="small"
                                        sx={{
                                            textTransform: 'capitalize',
                                            fontWeight: 'bold',
                                            borderRadius: 1.5,
                                            px: 1
                                        }}
                                    />
                                </TableCell>
                                <TableCell sx={{ color: 'text.secondary' }}>{new Date(user.createdAt).toLocaleDateString()}</TableCell>
                                <TableCell align="right">
                                    <IconButton size="small" onClick={() => handleEditUser(user)} sx={{ color: 'primary.main', mr: 1, bgcolor: 'primary.50', '&:hover': { bgcolor: 'primary.100' } }}>
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton size="small" color="error" onClick={() => handleDeleteUser(user._id)} sx={{ bgcolor: 'error.50', '&:hover': { bgcolor: 'error.100' } }}>
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                        {getFilteredUsers().length === 0 && (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                                    <Typography variant="h6" color="text.secondary" gutterBottom>
                                        Aucun utilisateur trouvé
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Essayez de modifier vos filtres ou votre recherche.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>


            <UserModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                onSave={handleSaveUser}
                initialData={editingUser || undefined}
                isEditing={!!editingUser}
            />

            {/* Delete Confirmation Modal */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                PaperProps={{
                    sx: { borderRadius: 3, p: 1 }
                }}
            >
                <DialogTitle sx={{ fontWeight: 'bold', color: 'error.main' }}>
                    Archiver l'utilisateur
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Êtes-vous sûr de vouloir archiver cet utilisateur ? Cette action désactivera son compte et l'empêchera de se connecter.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setDeleteDialogOpen(false)} variant="outlined" color="inherit">
                        Annuler
                    </Button>
                    <Button onClick={confirmDeleteUser} variant="contained" color="error" autoFocus>
                        Archiver
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Success Modal */}
            <Dialog
                open={successDialogOpen}
                onClose={() => setSuccessDialogOpen(false)}
                PaperProps={{
                    sx: { borderRadius: 3, p: 1 }
                }}
            >
                <DialogTitle sx={{ fontWeight: 'bold', color: 'success.main' }}>
                    Succès
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        L'utilisateur a été archivé avec succès.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setSuccessDialogOpen(false)} variant="contained" color="success">
                        Fermer
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default UsersPage;
